import { useDispatch } from "react-redux";
import Alertify from "../../../services/alertify";
import { PrintBulkQrCodes } from "../../../redux/actions/qrCodes/qrCodes-action";
import JSZip from "jszip";

const PrintBulkFunction = ({ selectedRows, setLoading }) => {
  const dispatch = useDispatch();

  const handlePrintBulk = () => {
    if (selectedRows.length === 0) {
      Alertify.error("Please select at least one QR code to print");
      return;
    }

    const ppfRollNumbers = selectedRows.map((row) => row.ppfRollNumber);

    setLoading(true);
    dispatch(PrintBulkQrCodes({ ppfRollNumbers }))
      .then((response) => {
        if (response?.payload?.data) {
          try {
            // Create a blob from the response data
            const blob = new Blob([response.payload.data], {
              type: "application/zip",
            });

            // Create a URL for the blob
            const blobUrl = URL.createObjectURL(blob);

            // Create a print window first to avoid popup blockers
            const printWindow = window.open("", "_blank");
            if (!printWindow) {
              Alertify.error(
                "Popup blocked. Please allow popups and try again."
              );

              // Fallback to download if popup is blocked
              const downloadLink = document.createElement("a");
              downloadLink.href = blobUrl;
              downloadLink.download = `QR_Codes_Bulk_${new Date()
                .toISOString()
                .slice(0, 10)}.zip`;
              document.body.appendChild(downloadLink);
              downloadLink.click();
              document.body.removeChild(downloadLink);
              setLoading(false);
              return;
            }

            // Write initial loading message
            printWindow.document.write(`
              <html>
              <head>
                <title>Loading QR Codes...</title>
                <style>
                  body { font-family: Arial, sans-serif; text-align: center; padding: 50px; }
                  .loader { border: 5px solid #f3f3f3; border-top: 5px solid #3498db; border-radius: 50%; width: 50px; height: 50px; animation: spin 2s linear infinite; margin: 20px auto; }
                  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
                </style>
              </head>
              <body>
                <h2>Loading QR Codes...</h2>
                <div class="loader"></div>
                <p>Please wait while we extract the QR codes for printing.</p>
              </body>
              </html>
            `);

            // Extract the ZIP file contents
            JSZip.loadAsync(blob)
              .then((zip) => {
                // Start writing the HTML document
                let htmlContent = `
                  <html>
                  <head>
                    <title>Print QR Codes</title>
                    <style>
                      @media print {
                        body { margin: 0; padding: 0; }
                        .no-print { display: none; }
                        .qr-grid { 
                          display: grid;
                          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                          gap: 10px;
                        }
                        .qr-item {
                          page-break-inside: avoid;
                        }
                        img { 
                          width: 200px;
                          margin: 0;
                          padding: 0;
                          border: none;
                        }
                      }
                      body {
                        margin: 0;
                        padding: 20px;
                        font-family: Arial, sans-serif;
                      }
                      .controls {
                        margin-bottom: 20px;
                        text-align: center;
                      }
                      button {
                        padding: 8px 16px;
                        background: #1890ff;
                        color: white;
                        border: none;
                        border-radius: 4px;
                        cursor: pointer;
                        margin: 0 5px;
                      }
                      .qr-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                        gap: 20px;
                      }
                      .qr-item {
                        text-align: center;
                      }
                      img {
                        width: 200px;
                        border: 1px solid #eee;
                      }
                      .qr-label {
                        margin-top: 5px;
                        font-size: 12px;
                      }
                    </style>
                  </head>
                  <body>
                    <div class="controls no-print">
                      <h2>QR Codes for Printing</h2>
                      <button onclick="window.print()">Print All QR Codes</button>
                      <button onclick="window.close()">Close</button>
                    </div>
                    <div class="qr-grid">
                `;

                // Filter image files
                const imageFiles = Object.keys(zip.files).filter(
                  (name) =>
                    !zip.files[name].dir &&
                    (name.endsWith(".png") || name.endsWith(".jpg"))
                );

                if (imageFiles.length === 0) {
                  printWindow.document.write(`
                    <html>
                    <head><title>Error</title></head>
                    <body>
                      <h2>No QR code images found in the ZIP file</h2>
                      <p>The ZIP file does not contain any PNG or JPG images.</p>
                      <button onclick="window.close()">Close</button>
                    </body>
                    </html>
                  `);
                  printWindow.document.close();
                  setLoading(false);
                  return;
                }

                // Process each image file
                let processedImages = 0;
                const imageDataUrls = [];

                imageFiles.forEach((filename) => {
                  const file = zip.files[filename];

                  // Extract the PPF roll number from the filename
                  const ppfRollNumber = filename.split("/").pop().split(".")[0];

                  // Get the file as a base64 data URL for more reliable handling
                  file
                    .async("base64")
                    .then((base64Data) => {
                      const extension = filename.endsWith(".png")
                        ? "png"
                        : "jpeg";
                      const dataUrl = `data:image/${extension};base64,${base64Data}`;

                      imageDataUrls.push({
                        url: dataUrl,
                        ppfRollNumber: ppfRollNumber,
                      });

                      processedImages++;

                      // If all images are processed, create the final HTML and write it to the window
                      if (processedImages === imageFiles.length) {
                        // Add all images to the HTML
                        imageDataUrls.forEach((image) => {
                          htmlContent += `
                          <div class="qr-item">
                            <img src="${image.url}" alt="QR Code for ${image.ppfRollNumber}">
                          </div>
                        `;
                        });

                        // Finish the HTML document
                        htmlContent += `
                        </div>
                        <script>
                          // Auto-print when all images are loaded
                          window.onload = function() {
                            // Small delay to ensure all images are rendered
                            setTimeout(() => {
                              window.print();
                            }, 1000);
                          };
                        </script>
                        </body>
                        </html>
                      `;

                        // Write the complete HTML to the window
                        printWindow.document.open();
                        printWindow.document.write(htmlContent);
                        printWindow.document.close();

                        setLoading(false);
                      }
                    })
                    .catch((err) => {
                      console.error("Error processing image:", filename, err);
                    });
                });
              })
              .catch((err) => {
                console.error("Error extracting ZIP:", err);
                printWindow.document.write(`
                  <html>
                  <head><title>Error</title></head>
                  <body>
                    <h2>Error Extracting QR Codes</h2>
                    <p>There was an error extracting the QR codes from the ZIP file.</p>
                    <p>Error details: ${err.message}</p>
                    <button onclick="window.close()">Close</button>
                  </body>
                  </html>
                `);
                printWindow.document.close();

                // Fallback to download
                Alertify.error(
                  "Error extracting QR codes. Downloading ZIP file instead."
                );
                const downloadLink = document.createElement("a");
                downloadLink.href = blobUrl;
                downloadLink.download = `QR_Codes_Bulk_${new Date()
                  .toISOString()
                  .slice(0, 10)}.zip`;
                document.body.appendChild(downloadLink);
                downloadLink.click();
                document.body.removeChild(downloadLink);

                setLoading(false);
              });
          } catch (error) {
            console.error("Error processing ZIP file:", error);
            Alertify.error("Error processing QR codes");
            setLoading(false);
          }
        } else {
          console.error("No data in response:", response);
          Alertify.error("Failed to generate QR codes");
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error generating bulk QR codes:", error);
        Alertify.error("Error generating bulk QR codes");
        setLoading(false);
      });
  };

  return { handlePrintBulk };
};

export default PrintBulkFunction;
